import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../setupAxios";
import Badge from "./Badge";
import { usePaginacion, Paginador } from "./Paginacion";
import { onActualizarPanel, onCambioResumen, avisarCambioAlertas } from "../statsBus";
import { isoToEs } from "../fechas";

// Administración > 🔔 Alertas: situaciones de llaves que hay que revisar
// (las detecta el backend solo, ver back_app/blueprints/alertas.py):
//   - un cliente o alguien de personal se ha dado de baja y tiene una llave,
//   - un piso se ha dado de baja y sigue con llaves,
//   - una asistenta tiene la llave de un piso que no es suyo.
// Cada una se puede marcar como "vista" (lo sé, sigue pendiente) o
// "correcta" (está bien así, deja de avisar); se resuelve sola cuando la
// situación cambia (se devuelve la llave, se reactiva a la persona…).
export const TIPOS_ALERTA = {
  baja_cliente: { label: "Cliente de baja con llave", corto: "Cliente de baja", ico: "🏠", tono: "red" },
  baja_personal: { label: "Personal de baja con llave", corto: "Personal de baja", ico: "👥", tono: "red" },
  baja_piso: { label: "Piso de baja con llaves", corto: "Piso de baja", ico: "🏢", tono: "red" },
  otro_piso: { label: "Llave de un piso que no es suyo", corto: "Llave de otro piso", ico: "🔀", tono: "amber" },
};
const ESTADOS = [
  ["pendientes", "Pendientes"], ["nuevas", "🆕 Nuevas"], ["vistas", "👁 Vistas"],
  ["correctas", "✓ Correctas"], ["resueltas", "✅ Resueltas"], ["todas", "Todas"],
];

const fechaHora = (v) => (v ? new Date(v).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—");

// El detalle viene como "Motivo · sus pisos: A, B, C": cada parte en su
// línea, y la lista de pisos como etiquetas que bajan de línea si no caben.
function DetalleAlerta({ texto }) {
  const partes = String(texto || "").split(" · ");
  return (
    <>
      {partes.map((p, i) => {
        const m = p.match(/^sus pisos:\s*(.*)$/i);
        if (!m) return <div key={i} className={i === 0 ? "al-det-motivo" : "al-det-linea"}>{p}</div>;
        const pisos = m[1].split(",").map((x) => x.trim()).filter(Boolean);
        return (
          <div key={i} className="al-det-linea">
            <span className="muted">Sus pisos:</span>{" "}
            {pisos.length && pisos[0] !== "ninguno"
              ? <span className="al-det-pisos">{pisos.map((x) => <span key={x} className="al-det-piso">{x}</span>)}</span>
              : <b>ninguno</b>}
          </div>
        );
      })}
    </>
  );
}

// Sustituciones que esa asistenta ha hecho (o hará) en el piso de la llave.
const ESTADO_SUST = {
  terminada: { txt: "Terminada", cls: "terminada" },
  en_curso: { txt: "En curso", cls: "en-curso" },
  proxima: { txt: "Próxima", cls: "proxima" },
};
function rangoSust(x) {
  const i = x.inicio ? isoToEs(x.inicio) : "¿?";
  if (!x.fin) return `desde el ${i} (sin fecha de fin)`;
  return x.inicio === x.fin ? `el ${i}` : `del ${i} al ${isoToEs(x.fin)}`;
}
// ¿Tiene la llave por una sustitución que ha hecho (o hará) en ese piso?
export const porSustitucion = (r) => r.tipo === "otro_piso" && (r.sustituciones || []).length > 0;
// Frase corta de la sustitución más reciente, para el aviso destacado.
function resumenSustitucion(lista) {
  const x = lista[0];
  if (x.estado === "en_curso") return `Está haciendo una sustitución en este piso ${rangoSust(x)}`;
  if (x.estado === "proxima") return `Va a hacer una sustitución en este piso ${rangoSust(x)}`;
  return `Hizo una sustitución en este piso ${rangoSust(x)}, ya terminada: debería haberla devuelto`;
}

// Una sola línea, a todo lo ancho bajo la alerta: la sustitución más
// reciente en ese piso (y cuántas más hay, con sus fechas en el título).
function LineaSustitucion({ lista }) {
  const x = lista[0];
  const e = ESTADO_SUST[x.estado] || ESTADO_SUST.terminada;
  const otras = lista.slice(1);
  return (
    <div className="al-sust-linea" title={otras.length ? `Otras sustituciones en este piso: ${otras.map(rangoSust).join(" · ")}` : undefined}>
      <span className="al-sust-tag">🔄 Llave por sustitución</span>
      <span>{resumenSustitucion(lista)}</span>
      <span className={`al-sust-estado ${e.cls}`}>{e.txt}</span>
      {x.titular && <span className="muted">titular {x.titular}</span>}
      {x.comentario && <span className="al-sust-com">“{x.comentario}”</span>}
      {otras.length > 0 && <span className="muted">· y {otras.length} sustitución(es) más</span>}
    </div>
  );
}

function EstadoAlerta({ a }) {
  if (a.fecha_resuelta) return <Badge tone="green">✅ Resuelta</Badge>;
  if (a.estado === "nueva") return <Badge tone="red">🆕 Nueva</Badge>;
  if (a.estado === "vista") return <Badge tone="amber">👁 Vista</Badge>;
  return <Badge tone="blue">✓ Correcta</Badge>;
}

export default function Alertas() {
  const [datos, setDatos] = useState(null);
  const [estado, setEstado] = useState("pendientes");
  const [tipo, setTipo] = useState("");
  const [q, setQ] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [notaModal, setNotaModal] = useState(null); // {ids, estado, nota}
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async (forzar = false) => {
    try {
      const { data } = await api.get("/alertas", { params: { estado, desde: desde || undefined, hasta: hasta || undefined, forzar: forzar ? 1 : undefined } });
      setDatos(data);
    } catch { setDatos({ rows: [], cuenta: {} }); }
  }, [estado, desde, hasta]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => onActualizarPanel(() => cargar(true)), [cargar]);
  useEffect(() => onCambioResumen(() => cargar(true)), [cargar]);
  useEffect(() => { setSel(new Set()); }, [estado, tipo, desde, hasta]);
  useEffect(() => { if (tipo && tipo !== "otro_piso") setSoloSust(""); }, [tipo]);

  const rows = useMemo(() => datos?.rows || [], [datos]);
  const [soloSust, setSoloSust] = useState(""); // "" | "si" | "no": llave de otro piso por sustitución o no
  const porTipo = useMemo(() => {
    const n = { sust_si: 0, sust_no: 0 };
    for (const r of rows) {
      n[r.tipo] = (n[r.tipo] || 0) + 1;
      if (r.tipo === "otro_piso") n[porSustitucion(r) ? "sust_si" : "sust_no"] += 1;
    }
    return n;
  }, [rows]);
  const filtradas = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((r) => (!tipo || r.tipo === tipo)
      && (!soloSust || (r.tipo === "otro_piso" && porSustitucion(r) === (soloSust === "si")))
      && (!t || [r.persona, r.nombre_crudo, r.piso, r.detalle, r.nota, r.id_piso, r.codigo_llave]
      .some((v) => String(v ?? "").toLowerCase().includes(t))));
  }, [rows, tipo, q, soloSust]);
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(filtradas, [rows, tipo, q, soloSust]);
  const cuenta = datos?.cuenta || {};

  const marcar = async (ids, nuevoEstado, nota = null) => {
    if (!ids.length) return;
    setOcupado(true);
    try {
      await api.post("/alertas/marcar", { ids, estado: nuevoEstado, nota });
      setSel(new Set());
      await cargar();
      avisarCambioAlertas();
    } finally { setOcupado(false); }
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const abiertasPagina = paginaRows.filter((r) => !r.fecha_resuelta);
  const todasMarcadas = abiertasPagina.length > 0 && abiertasPagina.every((r) => sel.has(r.id));
  const idsSel = [...sel];

  return (
    <div className="card alertas">
      <div className="card-headrow">
        <h3>🔔 Alertas de llaves</h3>
        <div className="card-headrow-acc">
          <span className="muted">{filtradas.length} de {rows.length} alerta(s)</span>
          <button className="btn" onClick={() => { setTipo(""); setQ(""); setDesde(""); setHasta(""); }}>Limpiar filtros</button>
        </div>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Se detectan solas: personas o pisos <b>dados de baja</b> que siguen con llaves, y <b>asistentas con la llave de un piso
        que no es suyo</b> (ni lo cubren hoy en una sustitución). Se resuelven solas al devolver la llave o cambiar la situación.
        Márcalas como <b>vista</b> (lo sé, sigue pendiente) o <b>correcta</b> (está bien así, deja de avisar).
      </p>

      <div className="al-estados">
        {ESTADOS.map(([k, label]) => (
          <button key={k} type="button" className={estado === k ? "on" : ""} onClick={() => setEstado(k)}>
            {label}{k !== "todas" && cuenta[k] != null && <span className="al-n">{cuenta[k]}</span>}
          </button>
        ))}
      </div>

      <div className="al-tipos">
        <button type="button" className={!tipo ? "on" : ""} onClick={() => setTipo("")}>Todos los tipos <span className="al-n">{rows.length}</span></button>
        {Object.entries(TIPOS_ALERTA).map(([k, t]) => (
          <button key={k} type="button" className={`t-${t.tono} ${tipo === k ? "on" : ""}`} onClick={() => setTipo(tipo === k ? "" : k)}>
            {t.ico} {t.corto} <span className="al-n">{porTipo[k] || 0}</span>
          </button>
        ))}
      </div>
      {(tipo === "" || tipo === "otro_piso") && porTipo.otro_piso > 0 && (
        <div className="al-tipos al-subtipos">
          <span className="muted">Llave de otro piso:</span>
          <button type="button" className={!soloSust ? "on" : ""} onClick={() => setSoloSust("")}>Todas</button>
          <button type="button" className={`t-sust ${soloSust === "si" ? "on" : ""}`} onClick={() => setSoloSust(soloSust === "si" ? "" : "si")}>
            🔄 Por sustitución <span className="al-n">{porTipo.sust_si}</span>
          </button>
          <button type="button" className={soloSust === "no" ? "on" : ""} onClick={() => setSoloSust(soloSust === "no" ? "" : "no")}>
            Sin sustitución <span className="al-n">{porTipo.sust_no}</span>
          </button>
        </div>
      )}

      <div className="al-filtros">
        <input className="al-buscar" placeholder="🔍 Buscar persona, piso, código, motivo…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="al-fecha">Detectada desde <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label className="al-fecha">hasta <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
      </div>

      {sel.size > 0 && (
        <div className="al-lote">
          <b>{sel.size} seleccionada(s)</b>
          <button type="button" className="al-btn al-btn-visto" disabled={ocupado} onClick={() => marcar(idsSel, "vista")}>👁 Marcar como vistas</button>
          <button type="button" className="al-btn al-btn-ok" disabled={ocupado} onClick={() => setNotaModal({ ids: idsSel, estado: "correcta", nota: "" })}>✓ Marcar como correctas</button>
          <button type="button" className="al-btn al-btn-reabrir" disabled={ocupado} onClick={() => marcar(idsSel, "nueva")}>↩ Reabrir</button>
          <button type="button" className="al-btn al-btn-quitar" onClick={() => setSel(new Set())}>✕ Quitar selección</button>
        </div>
      )}

      {!datos ? <p className="muted">Cargando…</p> : (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 30 }}>
                  <input type="checkbox" checked={todasMarcadas} disabled={!abiertasPagina.length} aria-label="Seleccionar todas las de esta página"
                         onChange={() => setSel((s) => { const n = new Set(s); abiertasPagina.forEach((r) => (todasMarcadas ? n.delete(r.id) : n.add(r.id))); return n; })} />
                </th>
                <th>Estado</th><th>Tipo</th><th>Detectada</th><th>Persona</th><th>Piso · llave</th><th>Prestada desde</th><th>Detalle</th><th></th>
              </tr>
            </thead>
            <tbody>
              {paginaRows.map((r) => {
                const t = TIPOS_ALERTA[r.tipo] || {};
                return (
                  <React.Fragment key={r.id}>
                  <tr className={`al-fila ${r.estado === "nueva" && !r.fecha_resuelta ? "al-nueva" : ""} ${porSustitucion(r) ? "al-fila-sust" : ""} ${r.fecha_resuelta ? "fila-apagada" : ""}`}>
                    <td>{!r.fecha_resuelta && <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} aria-label="Seleccionar" />}</td>
                    <td><EstadoAlerta a={r} /></td>
                    <td>
                      <span className={`al-tipo t-${t.tono}`}>{t.ico} {t.corto}</span>
                      {porSustitucion(r) && <div><span className="al-pill-sust">🔄 Por sustitución</span></div>}
                    </td>
                    <td className="nowrap">{fechaHora(r.fecha_detectada)}
                      {r.fecha_resuelta && <div className="muted" style={{ fontSize: 11 }}>resuelta {fechaHora(r.fecha_resuelta)}</div>}
                    </td>
                    <td>
                      {r.persona ? <b>{r.persona}</b> : <span className="muted">—</span>}
                      {r.tipo_persona && <div className="muted" style={{ fontSize: 11 }}>
                        {r.tipo_persona === "alumno" ? "Residente" : "Personal"}{r.id_persona ? ` · ID ${r.id_persona}` : ""}
                      </div>}
                    </td>
                    <td className="nowrap">
                      <span className="mono">{r.id_piso}{r.codigo_llave != null ? `-${r.codigo_llave}` : ""}</span>
                      <div style={{ fontSize: 12 }}>{r.piso || "—"}</div>
                    </td>
                    <td className="nowrap">{r.fecha_salida ? isoToEs(String(r.fecha_salida).slice(0, 10)) : "—"}
                      {r.estado_prestamo === "preparado" && <div><Badge tone="blue">preparada</Badge></div>}
                    </td>
                    <td className="al-detalle">
                      <DetalleAlerta texto={r.detalle} />
                      {r.nota && <div className="al-nota">📝 {r.nota}{r.revisada_por ? ` — ${r.revisada_por}` : ""}</div>}
                    </td>
                    <td>
                      {!r.fecha_resuelta && (
                        <div className="al-acciones">
                          {r.estado === "nueva" && (
                            <button type="button" className="al-btn al-btn-visto" disabled={ocupado} onClick={() => marcar([r.id], "vista")}
                                    title="Lo sé: sigue pendiente, pero deja de contar como nueva">
                              <span className="al-btn-ico">👁</span><span><b>Visto</b><small>sigue pendiente</small></span>
                            </button>
                          )}
                          {r.estado !== "correcta" && (
                            <button type="button" className="al-btn al-btn-ok" disabled={ocupado}
                                    onClick={() => setNotaModal({ ids: [r.id], estado: "correcta", nota: r.nota || "" })}
                                    title="Está bien así (p.ej. la cubre con permiso): deja de avisar">
                              <span className="al-btn-ico">✓</span><span><b>Correcto</b><small>deja de avisar</small></span>
                            </button>
                          )}
                          {r.estado !== "nueva" && (
                            <button type="button" className="al-btn al-btn-reabrir" disabled={ocupado} onClick={() => marcar([r.id], "nueva")}
                                    title="Volver a ponerla como nueva">
                              <span className="al-btn-ico">↩</span><span><b>Reabrir</b><small>como nueva</small></span>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {porSustitucion(r) && (
                    <tr className={`al-sust-tr ${r.fecha_resuelta ? "fila-apagada" : ""}`}>
                      <td colSpan={9}><LineaSustitucion lista={r.sustituciones} /></td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {filtradas.length === 0 && (
                <tr><td colSpan={9} className="empty">{estado === "pendientes" && !tipo && !q ? "🎉 No hay alertas pendientes." : "🗂️ Sin resultados."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />

      {notaModal && (
        <div className="modal-back" onMouseDown={() => setNotaModal(null)}>
          <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
            <h3>✓ Marcar como correcta{notaModal.ids.length > 1 ? `s (${notaModal.ids.length})` : ""}</h3>
            <p className="muted" style={{ marginTop: 0 }}>Deja de avisar mientras la situación siga igual. Si cambia (p.ej. se devuelve y se vuelve a prestar), volverá a salir.</p>
            <textarea rows={3} maxLength={500} autoFocus placeholder="Motivo (opcional): p.ej. la cubre esta semana, autorizado por…"
                      value={notaModal.nota} onChange={(e) => setNotaModal((m) => ({ ...m, nota: e.target.value }))} style={{ width: "100%" }} />
            <div className="modal-actions">
              <button className="btn" onClick={() => setNotaModal(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={ocupado}
                      onClick={async () => { const m = notaModal; setNotaModal(null); await marcar(m.ids, m.estado, m.nota); }}>
                Marcar correcta{notaModal.ids.length > 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
